import * as React from 'react';
import Box from '@mui/material/Box';
import { DataGrid } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import CalculateIcon from '@mui/icons-material/Calculate';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import DownloadIcon from '@mui/icons-material/Download';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';
import apiClient from '../ApiClient/ApiClient';

const vendorCollator = new Intl.Collator('zh-Hant-TW', {
  usage: 'sort',
  sensitivity: 'base',
  numeric: true,
});


const columns2 = [
    { field: 'id', headerName: 'ID', width: 60 },
    {
      field: 'billNo',
      headerName: '發票No',
      width: 150,
      editable: true,
    },
    {
      field: 'date',
      headerName: '日期',
      width: 150,
      editable: true,
    },
    {
      field: 'orderNum',
      headerName: '單號',
      width: 150,
      editable: true,
    },
    {
      field: 'piNum',
      headerName: '批號',
      width: 150,
      editable: true,
    },
    {
      field: 'drawNum',
      headerName: '圖號',
      width: 150,
      editable: true,
    },
    {
        field: 'itemName',
        headerName: '物品名',
        width: 100,
        editable: true,
    },
    {
        field: 'amount',
        headerName: '數量',
        type:'number',
        width: 150,
        editable: true,
    },
    {
        field: 'price',
        headerName: '單價',
        type:'number',
        width: 100,
        editable: true,
    },
    {
        field: 'money',
        headerName: '金額',
        type:'number',
        width: 100,
        editable: true,
    }
  ];

export default function ShouldPay() {
    const [editedRows, setEditedRows] = React.useState([]);
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const day = String(currentDate.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
    const [value, setValue] = React.useState(dayjs(formattedDate));

    const [shouldPayRows,setShouldPayRows] = React.useState([]);
    const [bills,setBills] = React.useState([]);

    const [orderBy,setOrderBy] = React.useState('單號');
    const [total,setTotal] = React.useState(null);
    const [makeCom, setMakeCompany] = React.useState('');
    const [payableAmount, setPayableAmount] = React.useState(0);
    const [taxAmount, setTaxAmount] = React.useState(0);
    const [adjustmentAmount, setAdjustmentAmount] = React.useState(0);
    const [totalPayable, setTotalPayable] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(50); // 使用狀態來控制每頁顯示的行數
    const [canEditShouldPay, setCanEditShouldPay] = React.useState(false);
    const [canUnlockShouldPay, setCanUnlockShouldPay] = React.useState(false);

    const sortedShouldPayRows = React.useMemo(() => [...shouldPayRows].sort((a, b) => {
      const vendorA = String(a?.makeCom ?? '').trim();
      const vendorB = String(b?.makeCom ?? '').trim();
      const isTotalA = vendorA === '合計';
      const isTotalB = vendorB === '合計';

      if (isTotalA !== isTotalB) return isTotalA ? 1 : -1;
      if (!vendorA && vendorB) return 1;
      if (vendorA && !vendorB) return -1;

      const vendorOrder = vendorCollator.compare(vendorA, vendorB);
      if (vendorOrder !== 0) return vendorOrder;
      return Number(a?.id ?? 0) - Number(b?.id ?? 0);
    }), [shouldPayRows]);

    const getErrorMessage = (error, fallback) =>
      error?.response?.data?.message || error?.response?.data || error?.message || fallback;

    const handleLockRow = async (row) => {
      if (!window.confirm(`確定鎖定「${row.makeCom}」的應付帳款嗎？`)) return;
      setLoading(true);
      try {
        const response = await apiClient.post('/Bill/LockShouldPay', { id: row.id });
        alert(response.data?.message || '鎖帳完成');
        await handleSearch();
      } catch (error) {
        alert(getErrorMessage(error, '鎖帳失敗'));
      } finally {
        setLoading(false);
      }
    };

    const handleUnlockRow = async (row) => {
      if (!window.confirm(`確定解除「${row.makeCom}」的鎖帳嗎？`)) return;
      setLoading(true);
      try {
        const response = await apiClient.post('/Bill/UnlockShouldPay', { id: row.id });
        alert(response.data?.message || '已解除鎖帳');
        await handleSearch();
      } catch (error) {
        alert(getErrorMessage(error, '解除鎖帳失敗'));
      } finally {
        setLoading(false);
      }
    };

    const columns = [
      { field: 'id', headerName: 'ID', width: 90 },
      {
        field: 'makeCom',
        headerName: '廠商',
        width: 150,
        editable: true,
      },
      {
        field: 'money',
        headerName: '金額',
        type: 'number',
        width: 120,
        editable: true,
      },
      {
        field: 'tax',
        headerName: '稅額',
        type: 'number',
        width: 120,
        editable: true,
      },
      {
        field: 'changeMoney',
        headerName: '加減金額',
        type: 'number',
        width: 120,
        editable: true,
      },
      {
        field: 'total',
        headerName: '總計',
        type: 'number',
        width: 120,
        editable: true,
      },
      {
        field: 'memo',
        headerName: 'Memo',
        width: 300,
        editable: true,
      },
      {
        field: 'recalculate',
        headerName: '重計',
        width: 100,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Button
            variant="outlined"
            size="small"
            color="primary"
            onClick={() => handleRecalculateRow(params.row)}
            disabled={!canEditShouldPay || params.row.isLocked || params.row.makeCom === '合計'}
          >
            重計
          </Button>
        ),
      },
      {
        field: 'lockStatus',
        headerName: '鎖帳狀態',
        width: 190,
        sortable: false,
        valueGetter: (params) => {
          if (!params.row.isLocked) return '未鎖帳';
          const lockedAt = params.row.lastLockedAt
            ? dayjs(params.row.lastLockedAt).format('YYYY/MM/DD HH:mm')
            : '';
          return `已鎖帳${lockedAt ? ` ${lockedAt}` : ''}`;
        },
      },
      {
        field: 'lockAction',
        headerName: '鎖帳操作',
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          if (params.row.makeCom === '合計') return null;
          if (params.row.isLocked) {
            return canUnlockShouldPay ? (
              <Button size="small" color="warning" onClick={() => handleUnlockRow(params.row)}>
                解除
              </Button>
            ) : null;
          }
          return canEditShouldPay ? (
            <Button size="small" color="primary" onClick={() => handleLockRow(params.row)}>
              鎖帳
            </Button>
          ) : null;
        },
      },
      
    ];

    const [loading, setLoading] = React.useState(false);
    const handleSearch = async () => {
      setLoading(true);
      const rocYear = dayjs(value).year() - 1911;
      const formattedValue = `${rocYear}/${dayjs(value).format('MM')}`;

      try {
        const response = await apiClient.get(`/Bill/GetShouldPay?month=${formattedValue}`);
        console.log(response.data);
        if(response.data.statusCode === 200)  {
          const data = response.data.shouldPayTotalDtos;
            setShouldPayRows(data);
            setCanEditShouldPay(Boolean(response.data.canEditShouldPay));
            setCanUnlockShouldPay(Boolean(response.data.canUnlockShouldPay));

            if (data.length > 0) {
                const totalEntry = data.find((row) => String(row?.makeCom ?? '').trim() === '合計');
                setPayableAmount(totalEntry?.money ?? 0);
                setTaxAmount(totalEntry?.tax ?? 0);
                setAdjustmentAmount(totalEntry?.changeMoney ?? 0);
                setTotalPayable(totalEntry?.total ?? 0);
            } else {
                console.log('No data available');
                setPayableAmount(0);
                setTaxAmount(0);
                setAdjustmentAmount(0);
                setTotalPayable(0);
            }
        }
        // 在此处处理API返回的数据
      } catch (error) {
        console.error('There was an error!', error);
        // 在此处处理错误
      }finally{
        setLoading(false);
      }
    };

    const handleRecalculateRow = async (row) => {
      setLoading(true);
    
      // 計算民國年月（formattedValue）
      const rocYear = dayjs(value).year() - 1911;
      const formattedValue = `${rocYear}/${dayjs(value).format('MM')}`;
    
      try {
        // 呼叫後端 API 傳月份與廠商
        const response = await apiClient.post(`/Bill/RecalculateRow`, null, {
          params: {
            month: formattedValue,
            comid: row.id,
          }
        });
    
        if (response.status === 200 && response.data) {
          const { money, tax, total, memo } = response.data;
    
          // 將回傳資料合併進 row
          const updatedRow = {
            ...row,
            money,
            tax,
            total,
            memo
          };
    
          // 更新 shouldPayRows
          setShouldPayRows((prevRows) =>
            prevRows.map((r) => (r.id === row.id ? updatedRow : r))
          );
    
          // 更新 editedRows
          setEditedRows((prevEdited) => {
            const exists = prevEdited.find((r) => r.id === row.id);
            if (exists) {
              return prevEdited.map((r) => (r.id === row.id ? updatedRow : r));
            } 
              return [...prevEdited, updatedRow];
            
          });
        } else {
          console.error("無效的後端回傳資料");
        }
      } catch (error) {
        console.error("後端計算錯誤:", error);
        alert(getErrorMessage(error, '單筆重新計算失敗'));
      } finally {
        setLoading(false);
      }
    };
    
    

    const handleReCaculate = async () => {
      setLoading(true);
      const rocYear = dayjs(value).year() - 1911;
      const formattedValue = `${rocYear}/${dayjs(value).format('MM')}`;
      try {
        const response = await apiClient.post(`/Bill/Recalculate?month=${formattedValue}`);
        console.log(response.data);
        if(response.status === 200)  {
          handleSearch();
        }
        // 在此处处理API返回的数据
      } catch (error) {
        console.error('There was an error!', error);
        alert(getErrorMessage(error, '重新計算失敗'));
      }finally{
        setLoading(false);
      }
    };
    const processRowUpdate = (newRow, oldRow) => {
      // 透過 newRow 的 id 找到 editedRows 陣列中的索引
      const index = editedRows.findIndex(row => row.id === newRow.id);
    
      // 若找到相同的 id，則先刪除
      if (index > -1) {
        editedRows.splice(index, 1);
      }
    
      // 將 newRow 加入 editedRows 陣列
      setEditedRows([...editedRows, newRow]);
      return newRow;
    };
  
    const handleSave = async () => {
      if (!canEditShouldPay || editedRows.length === 0) return;
      setLoading(true);
      try {
        await apiClient.post(`/Bill/EditShouldPay`, editedRows);
        setEditedRows([]);
        await handleSearch();
        alert('保存完成');
      } catch (error) {
        alert(getErrorMessage(error, '保存失敗'));
      } finally {
        setLoading(false);
      }
    };

    const handleInStockSearch = async (params) => {
      setLoading(true);
      const rocYear = dayjs(value).year() - 1911;
      const formattedValue = `${rocYear}/${dayjs(value).format('MM')}`;
      setMakeCompany (params.row.makeCom);
      console.log(makeCom);
      try {
        const response = await apiClient.get(`/Bill/GetInstock?month=${formattedValue}&company=${params.row.makeCom}&orderby=${orderBy}`);
        console.log(response.data);
        if(response.data.statusCode === 200)  {
          setBills(response.data.inStockDtos)
          setTotal(response.data.totalNum)
        }
        // 在此处处理API返回的数据
      } catch (error) {
        console.error('There was an error!', error);
        // 在此处处理错误
      }finally{
        setLoading(false);
      }
    };

    const handleGetInstokMf = async (params) => {
      setLoading(true);
      // 计算 ROC 年和格式化日期
      console.log('handleGetInstokMf 被触发了');
      const rocYear = dayjs(value).year() - 1911;
      const formattedValue = `${rocYear}/${dayjs(value).format('MM')}`;
      try {
          // 发起请求，注意设置 responseType 为 'blob'
          const response = await apiClient.get(`/Bill/GetInstockMf?month=${formattedValue}&company=${makeCom}&orderby=${orderBy}`, {
              responseType: 'blob'
          });
  
          // 检查状态码并处理 PDF 文件
          if (response.status === 200) {
              // 创建 Blob URL 并在新标签页打开
              const fileURL = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
              window.open(fileURL, '_blank');
          } else {
              console.error('Failed to load PDF:', response.status);
          }
      } catch (error) {
          console.error('There was an error fetching the PDF:', error);
      }finally{
        setLoading(false);
      }
    };
  
    const handleshouldPayList = async () => {
      setLoading(true);
    // 计算 ROC 年和格式化日期
    
      const rocYear = dayjs(value).year() - 1911;
      const formattedValue = `${rocYear}/${dayjs(value).format('MM')}`;
       
      try{
      // 发起请求，注意设置 responseType 为 'blob'
      const response = await apiClient.get(`/Bill/GetShouldPayList?month=${formattedValue}`, { responseType: 'blob' });


        // 检查状态码并处理 PDF 文件
        if (response.status === 200) {
            // 创建 Blob URL 并在新标签页打开
            const fileURL = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            window.open(fileURL, '_blank');
        } else {
            console.error('Failed to load PDF:', response.status);
        }
      } catch (error) {
        console.error('There was an error fetching the PDF:', error);
    }finally{
      setLoading(false);
    }
};

    const handleShouldPayExcel = async () => {
      if (editedRows.length > 0) {
        alert('目前有尚未保存的修改，請先保存後再匯出。');
        return;
      }

      setLoading(true);
      const rocYear = dayjs(value).year() - 1911;
      const formattedValue = `${rocYear}/${dayjs(value).format('MM')}`;

      try {
        const response = await apiClient.get(
          `/Bill/GetShouldPayExcel?month=${encodeURIComponent(formattedValue)}`,
          { responseType: 'blob' }
        );
        const blob = new Blob(
          [response.data],
          { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
        );
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `應付帳款總表_${formattedValue.replace('/', '')}.xlsx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        let message = '匯出應付帳款總表失敗';
        if (error?.response?.data instanceof Blob) {
          const errorText = await error.response.data.text();
          try {
            message = JSON.parse(errorText)?.message || errorText || message;
          } catch {
            message = errorText || message;
          }
        } else {
          message = getErrorMessage(error, message);
        }
        alert(message);
      } finally {
        setLoading(false);
      }
    };

const handleDownloadExcel = async () => {
  setLoading(true);
  const rocYear = dayjs(value).year() - 1911;
  const formattedValue = `${rocYear}/${dayjs(value).format('MM')}`;

  try {
    const response = await apiClient.get(
      `/Bill/GetInstockExcel?month=${formattedValue}&company=${makeCom}&orderby=${orderBy}`,
      { responseType: 'blob' } // 很重要！用 blob 下載二進位檔
    );

    if (response.status === 200) {
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `對帳單_${makeCom}_${formattedValue}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } else {
      console.error('下載 Excel 失敗:', response.status);
    }
  } catch (error) {
    console.error('發生錯誤:', error);
  } finally {
    setLoading(false);
  }
};

const sortFieldMap = {
  '單號': 'orderNum',
  '發票號碼': 'billNo',
  '批號': 'piNum',
  '圖號': 'drawNum',
};

const sortedBills = React.useMemo(() => {
  const field = sortFieldMap[orderBy] ?? 'orderNum';

  // 複製一份，避免直接改動 state
  const arr = [...bills];

  arr.sort((a, b) => {
    const av = a?.[field];
    const bv = b?.[field];

    // 先處理空值
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;

    // 數字就用數字比
    if (typeof av === 'number' && typeof bv === 'number') return av - bv;

    // 其他一律當字串（支援中文/數字混合）
    return String(av).localeCompare(String(bv), 'zh-Hant', { numeric: true });
  });

  return arr;
}, [bills, orderBy]);



    console.log(value.format('YYYY-MM-DD'))
    return (
        <>
        <Box sx={{ height: 400, width: '100%' }}>
            <Box sx={{ '& button': { m: 1 } }}>
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={loading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
                <div>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DemoContainer components={['DatePicker', 'DatePicker']}>
                            <DatePicker
                            label="月份選擇"
                            value={value}
                            onChange={(newValue) => setValue(newValue)}
                            />
                        </DemoContainer>
                    </LocalizationProvider>
                    <Button variant="contained" endIcon={<SendIcon />} onClick={handleSearch}>
                        搜尋
                    </Button>
                    <Button variant="contained" endIcon={<CalculateIcon />} onClick={handleReCaculate}
                      disabled={!canEditShouldPay || shouldPayRows.some((row) => row.isLocked)}>
                        重新計算
                    </Button>
                    <Button variant="contained" onClick={handleSave} endIcon={<SaveIcon />}
                      disabled={!canEditShouldPay || editedRows.length === 0}>
                        保存
                    </Button>
                    <Button variant="contained" onClick={handleshouldPayList}  endIcon={<FactCheckIcon />}>
                        列印總表
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleShouldPayExcel}
                      endIcon={<DownloadIcon />}
                      disabled={!canEditShouldPay || shouldPayRows.length === 0 || editedRows.length > 0}
                    >
                        匯出 Excel
                    </Button>
                    <div>應付金額：{payableAmount.toLocaleString('zh-TW', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    <div>稅額：{taxAmount.toLocaleString('zh-TW', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    <div>加減金額：{adjustmentAmount.toLocaleString('zh-TW', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    <div>應付總計：{totalPayable.toLocaleString('zh-TW', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>

                </div>
            </Box>
        <DataGrid
            rows={sortedShouldPayRows}
            columns={columns.map(column => ({ ...column, sortable: false }))}
            onCellClick={handleInStockSearch}
            initialState={{
            pagination: {
                paginationModel: {
                pageSize: 50,
                },
            },
            }}
            rowHeight={30}
            headerHeight={32} 
            pageSize={pageSize} // 綁定到 pageSize state
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)} // 動態更新 pageSize
            processRowUpdate={processRowUpdate}
            isCellEditable={(params) => canEditShouldPay && !params.row.isLocked && params.row.makeCom !== '合計'}
            onProcessRowUpdateError={error=>alert(error)}
            onRowEditCommit={(rowId, e) => {
              handleSave(rowId,e);
            }}
            pageSizeOptions={[50,75,100]}
            disableRowSelectionOnClick
        />
        </Box>
        
        <Box sx={{ height: 400, width: '100%', marginTop: '16%' }}>
    <FormControl fullWidth>
        <FormLabel id="demo-row-radio-buttons-group-label" sx={{ marginTop: '16px' }}>排序</FormLabel>
        <RadioGroup
            row
            aria-labelledby="demo-row-radio-buttons-group-label"
            name="row-radio-buttons-group"
            value={orderBy}
            onChange={(e) => setOrderBy(e.target.value)}
        >
            <FormControlLabel value="單號" control={<Radio />} label="單號" />
            <FormControlLabel value="發票號碼" control={<Radio />} label="發票號碼" />
            <FormControlLabel value="批號" control={<Radio />} label="批號" />
            <FormControlLabel value="圖號" control={<Radio />} label="圖號" />
        </RadioGroup>
        <Box sx={{ display: 'flex', gap: 2, marginTop: '16px' }}>
        <Button
            variant="contained"
            endIcon={<FactCheckIcon />}
            sx={{ width: '30%', marginTop: '16px' }}
            onClick={handleGetInstokMf}  // 更新了 onClick 事件
        >
            對帳單
        </Button>
        <Button
            variant="contained"
            color="success"
            sx={{ width: '30%', marginTop: '16px', marginLeft: '2%' }}
            onClick={handleDownloadExcel}
        >
            下載EXCEL
        </Button>
        </Box>
        <div style={{ marginTop: '10px' }}>
        <span>合計 : </span><span>{total !== null ? total.total.toFixed(2) : null}</span>
        <span> 營業稅 : </span><span>{total !== null ? total.tax.toFixed(2) : null}</span>
        <span> 總計 : </span><span>{total !== null ? total.totalMoney.toFixed(2) : null}</span>

        </div>
    </FormControl>
    <DataGrid
        rows={sortedBills}
        columns={columns2}
        initialState={{
            pagination: {
                paginationModel: {
                    pageSize: 15,
                },
            },
        }}
        rowHeight={30}
        headerHeight={32} 
        pageSizeOptions={[5]}
        disableRowSelectionOnClick
    />
</Box>

        </>
    );
}
